// skills/cooking/cookingUI.js
// Cooking UI - Processing-style card layout

import { cookingSkill } from './cooking.js';
import { FOOD, TOOLS, EMPOWERED_RECIPES } from './cookingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatWithCommas } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getItemName, getItemIcon, getItem } from '../../data/items/index.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getEffectiveFoodHeal } from '../../ui/equipmentTooltip.js';
import { getTalentXPBonus, getTalentLootBonus } from '../../systems/talentTree/talentTree.js';
import { isBloomholdEligible } from '../../systems/bloomhold.js';
import { getSkillIcon } from '../../core/skillRegistry.js';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

// Current cooking tab
let currentCookingTab = 'cook'; // 'cook' | 'empower'

/**
 * Render compact header with tool upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.COOKING);
    const xpPerHour = getXPPerHour(SKILLS.COOKING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.COOKING);

    // Tool info
    const activeTool = cookingSkill.getActiveBuff();
    const tools = Object.values(TOOLS);
    const currentIndex = tools.findIndex(t => t.id === activeTool.id);
    const nextTool = tools[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextTool) {
        const canAfford = playerGold >= nextTool.price;
        if (skill.level < nextTool.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextTool.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.cookingUI.upgradeTool('${nextTool.id}')">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.COOKING)}</span>
                    <span class="compact-name">Cooking</span>
                    ${renderSkillInfo(SKILLS.COOKING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.COOKING)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Range:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render a single food card
 */
function renderFoodCard(food, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= food.level;
    const isActive = currentAction?.type === 'cooking' && currentAction?.food?.id === food.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = cookingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.COOKING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.COOKING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = food.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.COOKING);
    let effectiveXP = food.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier for drop quantities (talent + corruption)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.COOKING);
    const talentLootBonus = getTalentLootBonus(SKILLS.COOKING);

    // Check if player has required raw food
    const playerFoodCount = gameState.getItemCount(food.inputItem);
    const hasFood = playerFoodCount >= food.inputQuantity;

    // Input material display
    const inputIcon = getItemIcon(food.inputItem);
    const inputName = getItemName(food.inputItem);

    // Output display (cooked food)
    const drop = food.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const outputItem = getItem(drop.itemId);
    const baseHeal = outputItem?.healAmount || 0;
    const effectiveHeal = outputItem ? getEffectiveFoodHeal(outputItem) : 0;
    const baseQty = drop.quantity;
    const adjustedQty = talentLootBonus > 0 ? Math.max(1, Math.floor(baseQty * (1 + talentLootBonus))) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    const cardClasses = [
        'cooking-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasFood && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-food-id="${food.id}"
             style="--food-color: ${food.color}">

            <div class="cooking-card-header">
                <span class="cooking-card-level">${isUnlocked ? `Lvl ${food.level}` : `Req: ${food.level}`}</span>
                <div class="cooking-icon-wrapper">${outputIcon}</div>
                <span class="cooking-card-name">${food.name}</span>
            </div>

            <div class="cooking-card-info">
                <div class="cooking-stat">
                    <span class="stat-icon cooking-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="cooking-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="cooking-materials">
                <div class="materials-label">Input</div>
                <div class="material-row ${!hasFood ? 'missing' : ''}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">${inputName}</span>
                    <span class="material-count">${playerFoodCount}/${food.inputQuantity}</span>
                </div>
            </div>

            <div class="cooking-output">
                <div class="output-label">Output</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
                <div class="output-stats">
                    <span class="heal-amount">Heals: ${effectiveHeal > baseHeal ? `<span class="boosted">${effectiveHeal}</span> <span class="base-heal">(${baseHeal})</span>` : baseHeal} HP</span>
                </div>
            </div>

            <div class="cooking-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.cookingUI.toggleCooking('${food.id}')">
                        ${isActive ? 'Stop' : 'Cook'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="cooking-progress">
                <div class="cooking-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a single empower recipe card
 */
function renderEmpowerCard(recipe, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= recipe.level;
    const isActive = currentAction?.type === 'empowering' && currentAction?.recipe?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time with all buffs
    const speedMultiplier = cookingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.COOKING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.COOKING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with corruption then talent
    const talentXPBonus = getTalentXPBonus(SKILLS.COOKING);
    let effectiveXP = recipe.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Check all input items (inventory + BloomHold for eligible items)
    let hasAllInputs = true;
    const inputsHtml = recipe.inputs.map(input => {
        const inventoryQty = gameState.getItemCount(input.itemId);
        // Check BloomHold for eligible items (crops)
        const bloomholdQty = (gameState.isBloomholdUnlocked() && isBloomholdEligible(input.itemId))
            ? gameState.getBloomholdItemCount(input.itemId)
            : 0;
        const totalQty = inventoryQty + bloomholdQty;
        const hasEnough = totalQty >= input.quantity;
        if (!hasEnough) hasAllInputs = false;

        const inputIcon = getItemIcon(input.itemId);
        const inputName = getItemName(input.itemId);

        // Show breakdown when BloomHold has items: "170/5 (50+120🌱)"
        const countDisplay = bloomholdQty > 0
            ? `${totalQty}/${input.quantity} (${inventoryQty}+${bloomholdQty}🌱)`
            : `${totalQty}/${input.quantity}`;

        return `
            <div class="material-row ${!hasEnough ? 'missing' : ''}" data-item-id="${input.itemId}">
                <span class="material-icon">${inputIcon}</span>
                <span class="material-name">${inputName}</span>
                <span class="material-count">${countDisplay}</span>
            </div>
        `;
    }).join('');

    // Output display
    const outputIcon = getItemIcon(recipe.output.itemId);
    const outputName = getItemName(recipe.output.itemId);
    const outputItem = getItem(recipe.output.itemId);

    // Healing info (with HoT inline for tier 7-9)
    const baseHeal = outputItem?.healAmount || 0;
    const effectiveHeal = outputItem ? getEffectiveFoodHeal(outputItem) : 0;
    const healText = effectiveHeal > baseHeal ? `<span class="boosted">${effectiveHeal}</span> <span class="base-heal">(${baseHeal})</span>` : `${baseHeal}`;
    const hotText = outputItem?.hot ? ` + ${outputItem.hot.amount * outputItem.hot.ticks} HoT (${outputItem.hot.amount}/s)` : '';
    const healInfoHtml = `<span class="heal-amount">Heals: ${healText} HP${hotText}</span>`;
    const hotInfoHtml = '';

    const cardClasses = [
        'empower-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasAllInputs && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-recipe-id="${recipe.id}"
             style="--recipe-color: ${recipe.color}">

            <div class="empower-card-header">
                <span class="empower-card-level">${isUnlocked ? `Lvl ${recipe.level}` : `Req: ${recipe.level}`}</span>
                <div class="cooking-icon-wrapper">${outputIcon}</div>
                <span class="empower-card-name">${recipe.name}</span>
            </div>

            <div class="empower-card-info">
                <div class="empower-stat">
                    <span class="stat-icon cooking-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="empower-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="empower-materials">
                <div class="materials-label">Ingredients</div>
                ${inputsHtml}
            </div>

            <div class="empower-output">
                <div class="output-label">Output</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    <span class="output-qty">x${recipe.output.quantity}</span>
                </div>
                <div class="output-stats">
                    ${healInfoHtml}
                    ${hotInfoHtml}
                </div>
            </div>

            <div class="empower-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.cookingUI.toggleEmpowering('${recipe.id}')">
                        ${isActive ? 'Stop' : 'Empower'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="empower-progress">
                <div class="empower-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render cooking tabs
 */
function renderCookingTabs() {
    return `
        <div class="skill-tabs cooking-tabs">
            <button class="skill-tab ${currentCookingTab === 'cook' ? 'active' : ''}"
                    onclick="window.cookingUI.switchCookingTab('cook')">
                🍳 Cook
            </button>
            <button class="skill-tab ${currentCookingTab === 'empower' ? 'active' : ''}"
                    onclick="window.cookingUI.switchCookingTab('empower')">
                ✨ Empower
            </button>
        </div>
    `;
}

/**
 * Render the cooking interface
 */
export function renderCookingUI() {
    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.COOKING);
    const currentAction = gameState.getCurrentAction();

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.COOKING);

    let contentHtml = '';
    if (currentCookingTab === 'cook') {
        const foodHtml = Object.values(FOOD)
            .map(food => renderFoodCard(food, skill.level, currentAction))
            .join('');
        contentHtml = `<div class="cooking-grid">${foodHtml}</div>`;
    } else {
        const empowerHtml = Object.values(EMPOWERED_RECIPES)
            .map(recipe => renderEmpowerCard(recipe, skill.level, currentAction))
            .join('');
        contentHtml = `<div class="empower-grid">${empowerHtml}</div>`;
    }

    container.innerHTML = `
        <div class="cooking-interface">
            ${renderCompactHeader()}
            ${renderCookingTabs()}
            ${contentHtml}
        </div>
    `;
}

/**
 * Toggle cooking on/off for food
 */
export function toggleCooking(foodId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.food?.id === foodId) {
        cookingSkill.stopCooking();
    } else {
        const result = cookingSkill.canCook(foodId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            cookingSkill.startCooking(foodId);
        }
    }

    renderCookingUI();
}

/**
 * Upgrade tool
 */
export function upgradeTool(toolId) {
    const result = cookingSkill.upgradeTool(toolId);

    if (result.success) {
        renderCookingUI();
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Toggle empowering on/off for recipe
 */
export function toggleEmpowering(recipeId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.recipe?.id === recipeId) {
        cookingSkill.stopEmpowering();
    } else {
        const result = cookingSkill.canEmpower(recipeId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            cookingSkill.startEmpowering(recipeId);
        }
    }

    renderCookingUI();
}

/**
 * Switch cooking tab
 */
export function switchCookingTab(tab) {
    if (currentCookingTab === tab) return;
    currentCookingTab = tab;
    renderCookingUI();
}

/**
 * Update cooking UI each tick
 */
export function updateCookingUI() {
    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.COOKING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        if (!isTalentTreeOpen()) {
            renderCookingUI();
        }
        return;
    }

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.COOKING);
    const xpPerHour = getXPPerHour(SKILLS.COOKING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.COOKING);

    const xpTotalEl = document.querySelector('.cooking-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.cooking-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.cooking-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    // Update progress bar using helper
    updateCompactHeaderProgress('.cooking-interface', SKILLS.COOKING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.cooking-interface');

    // Update material counts on all visible cards
    updateMaterialCounts();

    const action = gameState.getCurrentAction();
    const isCookingActive = action && action.type === 'cooking';
    const isEmpoweringActive = action && action.type === 'empowering';

    // Check for stale progress bars (action stopped)
    if (currentCookingTab === 'cook') {
        const existingCards = document.querySelectorAll('.cooking-interface .cooking-card.active');
        if (existingCards.length > 0 && !isCookingActive) {
            renderCookingUI();
            return;
        }

        // Update action progress if cooking is active
        if (isCookingActive && action.food) {
            const activeCard = document.querySelector(`.cooking-card[data-food-id="${action.food.id}"]`);
            if (activeCard) {
                const cardProgressFill = activeCard.querySelector('.cooking-progress-fill');
                if (cardProgressFill) {
                    cardProgressFill.style.width = `${action.progress * 100}%`;
                }
            }
        }
    } else {
        // Empower tab
        const existingCards = document.querySelectorAll('.cooking-interface .empower-card.active');
        if (existingCards.length > 0 && !isEmpoweringActive) {
            renderCookingUI();
            return;
        }

        // Update action progress if empowering is active
        if (isEmpoweringActive && action.recipe) {
            const activeCard = document.querySelector(`.empower-card[data-recipe-id="${action.recipe.id}"]`);
            if (activeCard) {
                const cardProgressFill = activeCard.querySelector('.empower-progress-fill');
                if (cardProgressFill) {
                    cardProgressFill.style.width = `${action.progress * 100}%`;
                }
            }
        }
    }
}

/**
 * Update material counts on all visible cards
 */
function updateMaterialCounts() {
    // Update cooking cards
    const cookingCards = document.querySelectorAll('.cooking-interface .cooking-card');
    cookingCards.forEach(card => {
        const foodId = card.dataset.foodId;
        if (!foodId) return;

        const food = Object.values(FOOD).find(f => f.id === foodId);
        if (!food) return;

        const playerCount = gameState.getItemCount(food.inputItem);
        const hasMaterials = playerCount >= food.inputQuantity;

        const countEl = card.querySelector('.material-count');
        if (countEl) countEl.textContent = `${playerCount}/${food.inputQuantity}`;

        const materialRow = card.querySelector('.material-row');
        if (materialRow) materialRow.classList.toggle('missing', !hasMaterials);

        card.classList.toggle('no-materials', !hasMaterials && !card.classList.contains('locked'));
    });

    // Update empower cards
    const empowerCards = document.querySelectorAll('.cooking-interface .empower-card');
    empowerCards.forEach(card => {
        const recipeId = card.dataset.recipeId;
        if (!recipeId) return;

        const recipe = Object.values(EMPOWERED_RECIPES).find(r => r.id === recipeId);
        if (!recipe) return;

        // Check all input items for this recipe (inventory + BloomHold)
        let hasAllInputs = true;
        recipe.inputs.forEach(input => {
            const inventoryCount = gameState.getItemCount(input.itemId);
            // Check BloomHold for eligible items (crops)
            const bloomholdCount = (gameState.isBloomholdUnlocked() && isBloomholdEligible(input.itemId))
                ? gameState.getBloomholdItemCount(input.itemId)
                : 0;
            const totalCount = inventoryCount + bloomholdCount;
            const hasEnough = totalCount >= input.quantity;
            if (!hasEnough) hasAllInputs = false;

            // Update the specific material row
            const materialRow = card.querySelector(`.material-row[data-item-id="${input.itemId}"]`);
            if (materialRow) {
                const countEl = materialRow.querySelector('.material-count');
                // Show breakdown when BloomHold has items: "170/5 (50+120🌱)"
                const countDisplay = bloomholdCount > 0
                    ? `${totalCount}/${input.quantity} (${inventoryCount}+${bloomholdCount}🌱)`
                    : `${totalCount}/${input.quantity}`;
                if (countEl) countEl.textContent = countDisplay;
                materialRow.classList.toggle('missing', !hasEnough);
            }
        });

        card.classList.toggle('no-materials', !hasAllInputs && !card.classList.contains('locked'));
    });
}

/**
 * Setup cooking skill listeners
 */
export function initCookingUI() {
    cookingSkill.initializeStartingTool();
    cookingSkill.onCookingComplete = (food, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Cooking level ${xpResult.newLevel}!`);
        }

        // Only re-render if cooking page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.COOKING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderCookingUI();
            }
        }
    };

    // Empowering completion callback
    cookingSkill.onEmpoweringComplete = (recipe, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Cooking level ${xpResult.newLevel}!`);
        }

        // Only re-render if cooking page is currently being viewed and talent tree is not open
        if (getCurrentSkillId() === SKILLS.COOKING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderCookingUI();
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.cookingUI = {
        toggleCooking,
        upgradeTool,
        toggleEmpowering,
        switchCookingTab
    };
}

export default {
    renderCookingUI,
    toggleCooking,
    upgradeTool,
    toggleEmpowering,
    switchCookingTab,
    updateCookingUI,
    initCookingUI
};
